import { Box, FormLabel, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { number, object, string } from 'yup';
import { Button } from 'src/common';
import { RHFCustomSelect, RHFProvider, RHFTextField } from 'src/common/rhf';
import { organizationApi } from 'src/api/organizationApi';
import { pimApi } from 'src/api/pimApi';
import { GenericModal } from './modal';

export type CreateUserModalMode = 'create' | 'editCompany';

export interface CreateUserFormValues extends Record<string, unknown> {
  firstName: string;
  lastName: string;
  designation: string;
  language: string;
  productId: string;
  productSKU: string;
  numberOfDevices: number;
  description: string;
  organizationName: string;
  organizationTypeId: string;
  website: string;
  domainName: string;
  street: string;
  city: string;
  postalCode: string;
  province: string;
  country: string;
  timeZone: string;
  telephone: string;
  email: string;
}

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (_data: CreateUserFormValues) => void;
  mode?: CreateUserModalMode;
  initialValues?: Partial<CreateUserFormValues>;
  organizationTypeLabel?: string;
  modalTitle?: string;
  subtitle?: string;
  submitLabel?: string;
  userInformationTitle?: string;
  companyDetailsTitle?: string;
  languageOptions?: Array<{ id: string; label: string; value: string }>;
  packageOptions?: Array<{ id: string; label: string; value: string }>;
  organizationTypeOptions?: Array<{ id: string; label: string; value: string }>;
  countryOptions?: Array<{ id: string; label: string; value: string }>;
  timeZoneOptions?: Array<{ id: string; label: string; value: string }>;
}

type SelectOption = { id: string; label: string; value: string };

const DEFAULT_COUNTRY_OPTIONS: SelectOption[] = [
  { id: 'NL', label: 'Netherlands', value: 'NL' },
  { id: 'DE', label: 'Germany', value: 'DE' },
  { id: 'BE', label: 'Belgium', value: 'BE' },
  { id: 'US', label: 'USA', value: 'US' },
  { id: 'GB', label: 'UK', value: 'GB' },
];

const DEFAULT_TIMEZONE_OPTIONS: SelectOption[] = [
  { id: 'Europe/Amsterdam', label: 'Europe/Amsterdam', value: 'Europe/Amsterdam' },
  { id: 'Europe/Berlin', label: 'Europe/Berlin', value: 'Europe/Berlin' },
  { id: 'Europe/Brussels', label: 'Europe/Brussels', value: 'Europe/Brussels' },
  { id: 'America/New_York', label: 'America/New_York', value: 'America/New_York' },
  { id: 'Europe/London', label: 'Europe/London', value: 'Europe/London' },
];

const COUNTRY_DEFAULT_TIMEZONE: Record<string, string> = {
  NL: 'Europe/Amsterdam',
  DE: 'Europe/Berlin',
  BE: 'Europe/Brussels',
  US: 'America/New_York',
  GB: 'Europe/London',
};

const COUNTRY_VALUE_ALIASES: Record<string, string> = {
  NL: 'NL',
  Netherlands: 'NL',
  DE: 'DE',
  Germany: 'DE',
  BE: 'BE',
  Belgium: 'BE',
  US: 'US',
  USA: 'US',
  'United States': 'US',
  GB: 'GB',
  UK: 'GB',
  'United Kingdom': 'GB',
};

const normalizeCountryValue = (country?: string | null): string => {
  if (!country?.trim()) return '';
  const trimmed = country.trim();
  const matchedOption = DEFAULT_COUNTRY_OPTIONS.find(
    (option) => option.value === trimmed || option.label === trimmed
  );
  return matchedOption?.value ?? COUNTRY_VALUE_ALIASES[trimmed] ?? '';
};

const defaultValues: CreateUserFormValues = {
  firstName: '',
  lastName: '',
  designation: '',
  language: 'en-US',
  productId: '',
  productSKU: '',
  numberOfDevices: 1,
  description: '',
  organizationName: '',
  organizationTypeId: '',
  website: '',
  domainName: '',
  street: '',
  city: '',
  postalCode: '',
  province: '',
  country: 'US',
  timeZone: 'America/New_York',
  telephone: '',
  email: '',
};

const SectionTitle = ({
  title,
  icon,
}: {
  title: string;
  icon: React.ReactNode;
}) => (
  <Box display="flex" alignItems="center" marginBottom={2} gap={1.5}>
    <Box
      style={{
        width: 34,
        height: 34,
        borderRadius: 6,
        backgroundColor: '#E6F6FD',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      {icon}
    </Box>
    <Typography
      style={{
        fontSize: 32 / 2,
        fontWeight: 600,
        color: '#1A202C',
      }}>
      {title}
    </Typography>
  </Box>
);

// Regex for website URL
const WEBSITE_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w-./?%&=]*)?$/i;

const createUserValidationSchema = object({
  firstName: string().trim().required('First name is required'),
  lastName: string().trim().required('Last name is required'),
  designation: string().trim().optional(),
  language: string().trim().required('Language is required'),
  productId: string().trim().required('Product is required'),
  productSKU: string().trim().optional(),
  numberOfDevices: number()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return undefined;
      }
      const parsed = Number(originalValue);
      return Number.isNaN(parsed) ? value : parsed;
    })
    .typeError('Number of licenses must be a number')
    .required('Number of licenses is required')
    .integer('Number of licenses must be a whole number')
    .min(1, 'Number of licenses must be at least 1')
    .max(10, 'Number of licenses must be less than or equal to 10'),
  description: string().optional(),
  organizationName: string().trim().required('Organization name is required'),
  organizationTypeId: string().trim().required('Organization type is required'),
  website: string()
    .trim()
    .optional()
    .matches(WEBSITE_REGEX, {
      message: 'Enter a valid website URL',
      excludeEmptyString: true,
    }),
  domainName: string().trim().optional(),
  street: string().optional(),
  city: string().optional(),
  postalCode: string().optional(),
  province: string().optional(),
  country: string().optional(),
  timeZone: string().optional(),
  telephone: string().trim().required('Telephone is required'),
  email: string().trim().required('Email is required').email('Enter a valid email address'),
});

const editCompanyValidationSchema = object({
  description: string().optional(),
  organizationName: string().trim().required('Organization name is required'),
  website: string()
    .trim()
    .optional()
    .matches(WEBSITE_REGEX, {
      message: 'Enter a valid website URL',
      excludeEmptyString: true,
    }),
  domainName: string().trim().optional(),
  street: string().optional(),
  city: string().optional(),
  postalCode: string().optional(),
  province: string().optional(),
  country: string().optional(),
  timeZone: string().optional(),
  telephone: string().trim().required('Telephone is required'),
  email: string().trim().required('Email is required').email('Enter a valid email address'),
});

function useCreateUserFormUnit({
  onSubmit,
  onClose,
  mode = 'create',
  open,
  initialValues,
}: Pick<CreateUserModalProps, 'onSubmit' | 'onClose' | 'mode' | 'open' | 'initialValues'>) {
  const isEditCompany = mode === 'editCompany';

  const methods = useForm<CreateUserFormValues>({
    defaultValues,
    mode: 'onSubmit',
    resolver: yupResolver(
      isEditCompany ? editCompanyValidationSchema : createUserValidationSchema
    ) as any,
  });

  const getResetValues = (): CreateUserFormValues => {
    if (!(isEditCompany && initialValues)) return defaultValues;

    const country = normalizeCountryValue(initialValues.country) || initialValues.country || '';
    const timeZone =
      initialValues.timeZone || COUNTRY_DEFAULT_TIMEZONE[country] || defaultValues.timeZone;

    return {
      ...defaultValues,
      ...initialValues,
      country,
      timeZone,
    };
  };

  useEffect(() => {
    if (!open) return;

    methods.reset(getResetValues());
  }, [open, isEditCompany, initialValues]);

  const closeAndReset = () => {
    methods.reset(getResetValues());
    onClose();
  };

  const handleSubmit = methods.handleSubmit(async (values) => {
    await onSubmit?.(values);
    closeAndReset();
  });

  return {
    methods,
    closeAndReset,
    handleSubmit,
  };
}

const CreateUserModal = ({
  open,
  onClose,
  onSubmit,
  mode = 'create',
  initialValues,
  organizationTypeLabel,
  modalTitle,
  subtitle,
  submitLabel,
  userInformationTitle = 'User information',
  companyDetailsTitle,
  languageOptions = [
    { id: 'en-US', label: 'English (US)', value: 'en-US' },
    { id: 'nl-NL', label: 'Dutch (NL)', value: 'nl-NL' },
  ],
  packageOptions = [{ id: 'pro-backup-demo', label: 'Pro Backup Demo', value: 'pro-backup-demo' }],
  organizationTypeOptions = [
    { id: 'type-company', label: 'Company', value: 'company' },
    { id: 'type-partner', label: 'Partner', value: 'partner' },
  ],
  countryOptions = DEFAULT_COUNTRY_OPTIONS,
  timeZoneOptions = DEFAULT_TIMEZONE_OPTIONS,
}: CreateUserModalProps) => {
  const isEditCompany = mode === 'editCompany';
  const resolvedTitle = modalTitle ?? (isEditCompany ? 'Edit Client Company' : 'Create User');
  const resolvedSubtitle =
    subtitle ??
    (isEditCompany
      ? 'Update client company profile and location details.'
      : 'Configure system access and backup entitlements for the new organization');
  const resolvedSubmitLabel = submitLabel ?? (isEditCompany ? 'Save' : 'Create');
  const resolvedCompanyDetailsTitle =
    companyDetailsTitle ?? (isEditCompany ? 'Client Company Details' : 'Company details');

  const [dynamicPackageOptions, setDynamicPackageOptions] = useState<
    Array<{ id: string; label: string; value: string; sku?: string }>
  >([]);
  const [isLoadingPackageOptions, setIsLoadingPackageOptions] = useState(false);
  const [dynamicOrganizationTypeOptions, setDynamicOrganizationTypeOptions] = useState<
    Array<{ id: string; label: string; value: string }>
  >([]);
  const [isLoadingOrganizationTypes, setIsLoadingOrganizationTypes] = useState(false);

  const { methods, closeAndReset, handleSubmit } = useCreateUserFormUnit({
    onSubmit,
    onClose,
    mode,
    open,
    initialValues,
  });
  const {
    setValue,
    formState: { isSubmitting },
  } = methods;
  const resolvedPackageOptions =
    dynamicPackageOptions.length > 0 ? dynamicPackageOptions : packageOptions;
  const resolvedOrganizationTypeOptions = (
    dynamicOrganizationTypeOptions.length > 0
      ? dynamicOrganizationTypeOptions
      : organizationTypeOptions
  ).filter((option) => option.label.toLowerCase() !== 'reseller');

  const resolvedTimeZoneOptions = (() => {
    const currentTimezone = (initialValues?.timeZone || '').trim();
    if (
      currentTimezone &&
      !timeZoneOptions.some((option) => option.value === currentTimezone)
    ) {
      return [
        ...timeZoneOptions,
        { id: currentTimezone, label: currentTimezone, value: currentTimezone },
      ];
    }
    return timeZoneOptions;
  })();

  useEffect(() => {
    let mounted = true;

    const loadPackageOptions = async () => {
      if (!open || isEditCompany) {
        if (mounted) {
          setDynamicPackageOptions([]);
          setIsLoadingPackageOptions(false);
        }
        return;
      }

      setIsLoadingPackageOptions(true);

      try {
        const categoryResponse = await pimApi.getCategories({
          page: 0,
          pageSize: 100,
          search: 'Trial Accounts',
          isActive: true,
        });

        const firstCategory = categoryResponse?.data?.data?.[0];
        const categoryId = firstCategory?._id || firstCategory?.id;

        if (!categoryId) {
          if (mounted) {
            setDynamicPackageOptions([]);
          }
          return;
        }

        const productsResponse = await pimApi.getProductsByCategoryId(categoryId, {
          page: 0,
          pageSize: 100,
          isActive: true,
        });
        const products = productsResponse?.data?.data || [];
        const mappedOptions = products
          .map((product: Record<string, any>, index: number) => {
            const optionValue = String(product?._id || product?.id || `product-${index}`);
            const optionLabel = String(
              product?.productTemplateObject?.Name ||
              product?.name ||
              product?.productName ||
              optionValue
            );
            const optionSku =
              product?.sku ||
              product?.SKU ||
              product?.productTemplateObject?.SKU ||
              product?.productTemplateObject?.sku;

            return {
              id: optionValue,
              value: optionValue,
              label: optionLabel,
              sku: optionSku ? String(optionSku) : '',
            };
          })
          .filter((option: { label: string }) => option.label.trim().length > 0);

        if (mounted) {
          setDynamicPackageOptions(mappedOptions);
        }
      } catch (_error) {
        if (mounted) {
          setDynamicPackageOptions([]);
        }
      } finally {
        if (mounted) {
          setIsLoadingPackageOptions(false);
        }
      }
    };

    loadPackageOptions();

    return () => {
      mounted = false;
    };
  }, [open, isEditCompany]);

  useEffect(() => {
    let mounted = true;

    const loadOrganizationTypes = async () => {
      if (!open || isEditCompany) {
        if (mounted) {
          setDynamicOrganizationTypeOptions([]);
          setIsLoadingOrganizationTypes(false);
        }
        return;
      }

      setIsLoadingOrganizationTypes(true);

      try {
        const organizationTypeResponse = await organizationApi.getOrganizationTypes();
        const organizationTypes = Array.isArray(organizationTypeResponse?.data?.data)
          ? organizationTypeResponse.data.data
          : Array.isArray(organizationTypeResponse?.data)
            ? organizationTypeResponse.data
            : [];

        const mappedOptions = organizationTypes
          .map((item: Record<string, any>, index: number) => {
            const optionValue = String(item?.id || item?._id || item?.name || `org-type-${index}`);
            const optionLabel = String(item?.name || item?.label || optionValue);

            return {
              id: optionValue,
              value: optionValue,
              label: optionLabel,
            };
          })
          .filter((option: { label: string }) => option.label.trim().length > 0)
          .filter((option: { label: string }) => option.label.toLowerCase() !== 'reseller');

        if (mounted) {
          setDynamicOrganizationTypeOptions(mappedOptions);
        }
      } catch (_error) {
        if (mounted) {
          setDynamicOrganizationTypeOptions([]);
        }
      } finally {
        if (mounted) {
          setIsLoadingOrganizationTypes(false);
        }
      }
    };

    loadOrganizationTypes();

    return () => {
      mounted = false;
    };
  }, [open, isEditCompany]);

  const handleProductSelectionChange = (selectedProductId: string) => {
    const selectedOption = resolvedPackageOptions.find(
      (option) => option.value === selectedProductId
    ) as { sku?: string } | undefined;
    setValue('productSKU', selectedOption?.sku || '');
  };

  const handleCountryChange = (selectedCountry: string) => {
    const country = normalizeCountryValue(selectedCountry) || selectedCountry;
    const matchedTimezone = COUNTRY_DEFAULT_TIMEZONE[country];
    if (matchedTimezone) {
      setValue('timeZone', matchedTimezone, { shouldDirty: true, shouldValidate: true });
    }
  };

  const handleNumberOfDevicesKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === '-' || event.key === 'e' || event.key === 'E' || event.key === '+') {
      event.preventDefault();
    }
  };

  return (
    <GenericModal
      open={open}
      onClose={closeAndReset}
      title={resolvedTitle}
      width="1100px">
      <Typography style={{ color: '#6B7280', fontSize: 13, marginBottom: 20 }}>
        {resolvedSubtitle}
      </Typography>

      <RHFProvider methods={methods} onSubmit={handleSubmit}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box style={{ marginBottom: 20, border: '1px solid #EDF2F7', borderRadius: 8, padding: 20 }}>
            <SectionTitle
              title={resolvedCompanyDetailsTitle}
              icon={<BusinessOutlinedIcon style={{ color: '#17B5E4', fontSize: 20 }} />}
            />
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <RHFTextField required name="organizationName" outerLabel="ORGANIZATION NAME" placeholder="Organization Name" />
              {isEditCompany ? (
                <Box>
                  <FormLabel style={{ fontSize: 11, fontWeight: 600, color: '#718096', display: 'block', marginBottom: 8 }}>
                    ORGANIZATION TYPE
                  </FormLabel>
                  <Box
                    style={{
                      height: 50,
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 12px',
                      border: '1px solid #E2E8F0',
                      borderRadius: 8,
                      fontSize: 12,
                      color: '#64748B',
                      backgroundColor: '#F8FAFC',
                    }}>
                    {organizationTypeLabel ?? '—'}
                  </Box>
                </Box>
              ) : (
                <RHFCustomSelect
                  required
                  name="organizationTypeId"
                  outerLabel="ORGANIZATION TYPE"
                  options={resolvedOrganizationTypeOptions}
                  placeholder={isLoadingOrganizationTypes ? 'Loading organization types...' : 'Select type'}
                  disabled={isSubmitting || isLoadingOrganizationTypes}
                />
              )}
              <RHFTextField name="website" outerLabel="WEBSITE" placeholder="https://www.example.com" />
              {!isEditCompany && (
                <RHFTextField name="domainName" outerLabel="DOMAIN NAME" placeholder="Domain Name" />
              )}
            </Box>

            <Box
              style={{
                backgroundColor: '#F7FAFC',
                border: '1px solid #EDF2F7',
                borderRadius: 8,
                padding: 16,
                marginTop: 30,
                marginBottom: 30,
              }}>
              <FormLabel style={{ fontSize: 11, fontWeight: 600, color: '#718096' }}>
                LOCATION INFORMATION
              </FormLabel>
              <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} marginTop={2}>
                <RHFCustomSelect
                  name="country"
                  outerLabel="COUNTRY"
                  options={countryOptions}
                  placeholder="Select country"
                  onChangeCallback={handleCountryChange}
                />
                <RHFTextField name="city" outerLabel="CITY" placeholder="City" />
                <RHFTextField name="street" outerLabel="STREET" placeholder="Street" />
                <RHFTextField name="postalCode" outerLabel="POSTAL CODE" placeholder="Postal Code" />
                <RHFCustomSelect name="timeZone" outerLabel="TIME ZONE" options={resolvedTimeZoneOptions} placeholder="Select time zone" />
              </Box>
            </Box>

            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} marginTop={2}>
              <RHFTextField required name="telephone" outerLabel="TELEPHONE" placeholder="Enter phone number" />
              <RHFTextField required name="email" outerLabel="EMAIL" placeholder="Enter email" />
            </Box>
          </Box>

          {!isEditCompany && (
            <Box style={{ border: '1px solid #EDF2F7', borderRadius: 8, padding: 20, marginBottom: 20 }}>
              <SectionTitle
                title={userInformationTitle}
                icon={<PersonOutlineIcon style={{ color: '#17B5E4', fontSize: 20 }} />}
              />
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <RHFTextField required name="firstName" outerLabel="FIRST NAME" placeholder="Enter first name" />
                <RHFTextField required name="lastName" outerLabel="LAST NAME" placeholder="Enter last name" />
                <RHFTextField name="designation" outerLabel="DESIGNATION" placeholder="Enter designation" />
                <RHFCustomSelect name="language" outerLabel="LANGUAGE" options={languageOptions} placeholder="Select language" />
              </Box>
            </Box>
          )}

          {!isEditCompany && (
            <Box style={{ marginBottom: 20, border: '1px solid #EDF2F7', borderRadius: 8, padding: 20 }}>
              <SectionTitle
                title="Product details"
                icon={
                  <AccountBalanceWalletOutlinedIcon
                    style={{ color: '#17B5E4', fontSize: 20 }}
                  />
                }
              />
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <RHFCustomSelect
                  name="productId"
                  outerLabel="PRODUCT"
                  required
                  options={resolvedPackageOptions}
                  placeholder={isLoadingPackageOptions ? 'Loading products...' : 'Select product'}
                  disabled={isSubmitting || isLoadingPackageOptions}
                  onChangeCallback={handleProductSelectionChange}
                />
                <RHFTextField
                  name="numberOfDevices"
                  type="number"
                  required
                  outerLabel="NUMBER OF LICENSES"
                  placeholder="1"
                  inputProps={{ min: 1, max: 10, step: 1 }}
                  onKeyDown={handleNumberOfDevicesKeyDown}
                />
              </Box>
            </Box>
          )}

          <Box style={{ marginBottom: 20, border: '1px solid #EDF2F7', borderRadius: 8, padding: 20 }}>
            <SectionTitle
              title="Additional information"
              icon={<SubjectOutlinedIcon style={{ color: '#718096', fontSize: 20 }} />}
            />
            <RHFTextField
              name="description"
              outerLabel="DESCRIPTION"
              multiline
              minRows={3}
              placeholder="Provide any additional context or internal notes."
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              onClick={closeAndReset}
              variant="outlined"
              color="inherit"
              disabled={isSubmitting}
              sx={{ minWidth: '120px', height: '42px' }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
              sx={{
                minWidth: '150px',
                height: '42px',
                borderRadius: '8px',
                fontWeight: 700,
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
              }}>
              {resolvedSubmitLabel}
            </Button>
          </Box>
        </Box>
      </RHFProvider>
    </GenericModal>
  );
};

export default CreateUserModal;
